import { safeNumber } from "common/utils";
import { ProductSummary } from "types/model.types";

/**
 * Resolves the customer's each-price for a product based on their assigned
 * sell price level (1-4). Any level other than 2, 3 or 4 — including null,
 * undefined or 0 — falls back to sellPrice1. A level 2-4 price of exactly $0
 * is treated as "not set for this level" and also falls back to sellPrice1,
 * rather than charging the customer $0.
 */
export function getEachPriceForLevel(
  product: ProductSummary,
  sellPriceLevel: number | null | undefined,
): number {
  switch (sellPriceLevel) {
    case 2:
      return product.sellPrice2 || product.sellPrice1 || 0;
    case 3:
      return product.sellPrice3 || product.sellPrice1 || 0;
    case 4:
      return product.sellPrice4 || product.sellPrice1 || 0;
    default:
      return product.sellPrice1 ?? 0;
  }
}

/**
 * Computes the order-line unit price for a product given the customer's sell
 * price level and whether the line is ordered by Carton ("C") or Each ("E").
 *
 * Single source of truth for this calculation — used both for the row's
 * instant local preview (AdminOrderEntryRow) and the authoritative commit
 * (AdminOrderEntryPage.handleUpdateEntry) so the two can never diverge.
 */
export function computeUnitPrice(
  product: ProductSummary,
  sellPriceLevel: number | null | undefined,
  productCode: string,
): number {
  const eachPrice = getEachPriceForLevel(product, sellPriceLevel);
  const unitsCtn = product.unitsCtn || 1; // guard against 0 as well as null/undefined

  return safeNumber(productCode === "E" ? eachPrice : eachPrice * unitsCtn);
}
